// Default documents. System mails use these until the organizer customizes them (a
// mail_templates row with the same system_key). Starter templates stay here too; one is copied
// into mail_templates only when an organizer switches its automatic mail on or opens it to adjust
// (createFromStarter in app/components/Mail/Flow/flow.ts).
//
// What a buyer wants from each mail, in this order: did it work, what and when (the event block,
// which also carries the activity's own extra mail text), the tickets, how to get in, and who to
// ask. One short paragraph per thing; the organizer adds the rest.
import { MailDocument } from "./blocks.ts";

let n = 0;
const id = (prefix: string) => `${prefix}-${++n}`;

// Replies land in the organizer's inbox (Publiek > Berichten), so every mail may say so.
const replyLine = (prefix: string) => ({
  id: id(prefix), type: "text" as const, html: {
    nl: "<p>Vragen? Antwoord gewoon op deze mail, dan komt je bericht bij {{organizer.name}} terecht.</p>",
    en: "<p>Questions? Just reply to this e-mail and it will reach {{organizer.name}}.</p>",
    fr: "<p>Des questions ? Répondez simplement à cet e-mail, il arrivera chez {{organizer.name}}.</p>",
    de: "<p>Fragen? Antworte einfach auf diese E-Mail, dann landet sie bei {{organizer.name}}.</p>",
  },
});

export const SYSTEM_DEFAULTS: Record<string, MailDocument> = {
  order_confirmation: {
    version: 1,
    subject: {
      nl: "Je tickets voor {{event.title}}",
      en: "Your tickets for {{event.title}}",
      fr: "Vos billets pour {{event.title}}",
      de: "Deine Tickets für {{event.title}}",
    },
    // no {{visit.date}} here: an activity without a timeslot would read "See you !"
    preheader: {
      nl: "Gelukt, je bent erbij! Je tickets staan klaar.",
      en: "You're in! Your tickets are ready.",
      fr: "C'est fait, vous y serez ! Vos billets sont prêts.",
      de: "Geschafft, du bist dabei! Deine Tickets sind bereit.",
    },
    blocks: [
      {
        id: id("oc"), type: "text", html: {
          nl: "<p>Hoi {{buyer.first_name}},</p><p>Gelukt, je bent erbij! Hieronder staat alles wat je nodig hebt.</p>",
          en: "<p>Hi {{buyer.first_name}},</p><p>You're in! Below is everything you need.</p>",
          fr: "<p>Bonjour {{buyer.first_name}},</p><p>C'est fait, vous y serez ! Vous trouverez ci-dessous tout ce qu'il vous faut.</p>",
          de: "<p>Hallo {{buyer.first_name}},</p><p>Geschafft, du bist dabei! Unten findest du alles, was du brauchst.</p>",
        },
      },
      { id: id("oc"), type: "event" },
      { id: id("oc"), type: "tickets" },
      {
        id: id("oc"), type: "text", html: {
          nl: "<p><strong>Bij de ingang</strong> laat je je tickets zien op je telefoon, of je neemt ze geprint mee. Elk ticket heeft een eigen code die één keer wordt gescand, dus deel ze niet online.</p>",
          en: "<p><strong>At the entrance</strong> show your tickets on your phone, or bring them printed. Each ticket has its own code that is scanned once, so don't share them online.</p>",
          fr: "<p><strong>À l'entrée</strong>, montrez vos billets sur votre téléphone ou apportez-les imprimés. Chaque billet a son propre code, scanné une seule fois : ne les partagez pas en ligne.</p>",
          de: "<p><strong>Am Eingang</strong> zeigst du deine Tickets auf dem Handy oder bringst sie ausgedruckt mit. Jedes Ticket hat einen eigenen Code, der einmal gescannt wird, also teile sie nicht online.</p>",
        },
      },
    ],
  },
  voucher: {
    version: 1,
    subject: {
      nl: "Je tegoedbon van {{organizer.name}}",
      en: "Your voucher from {{organizer.name}}",
      fr: "Votre bon de {{organizer.name}}",
      de: "Dein Gutschein von {{organizer.name}}",
    },
    preheader: {
      nl: "Zelf gebruiken of cadeau doen: zo werkt het.",
      en: "Use it yourself or give it away: here's how.",
      fr: "Pour vous ou à offrir : voici comment ça marche.",
      de: "Selbst einlösen oder verschenken: so geht's.",
    },
    blocks: [
      {
        id: id("vo"), type: "text", html: {
          nl: "<p>Hoi {{buyer.first_name}},</p><p>Je tegoedbon zit als PDF in de bijlage. Zelf gebruiken of cadeau doen kan allebei: wie hem heeft, vult de code in bij het bestellen in de ticketshop.</p>",
          en: "<p>Hi {{buyer.first_name}},</p><p>Your voucher is attached as a PDF. Use it yourself or give it away: whoever has it enters the code when ordering in the ticket shop.</p>",
          fr: "<p>Bonjour {{buyer.first_name}},</p><p>Votre bon est joint en PDF. Pour vous ou à offrir : la personne qui l'a saisit le code en commandant dans la billetterie.</p>",
          de: "<p>Hallo {{buyer.first_name}},</p><p>Dein Gutschein ist als PDF im Anhang. Selbst einlösen oder verschenken: Wer ihn hat, gibt den Code beim Bestellen im Ticketshop ein.</p>",
        },
      },
      { id: id("vo"), type: "shop_button" },
      replyLine("vo"),
    ],
  },
  reservation_payment: {
    version: 1,
    subject: {
      nl: "Je reservering voor {{event.title}}",
      en: "Your reservation for {{event.title}}",
      fr: "Votre réservation pour {{event.title}}",
      de: "Deine Reservierung für {{event.title}}",
    },
    preheader: {
      nl: "Rond je reservering af met één klik.",
      en: "Complete your reservation in one click.",
      fr: "Finalisez votre réservation en un clic.",
      de: "Schließe deine Reservierung mit einem Klick ab.",
    },
    blocks: [
      {
        id: id("rp"), type: "text", html: {
          nl: "<p>Hoi {{buyer.first_name}},</p><p>{{organizer.name}} heeft tickets voor je vastgezet. Betaal via de knop hieronder, dan zijn ze definitief van jou en krijg je ze meteen in je mail.</p>",
          en: "<p>Hi {{buyer.first_name}},</p><p>{{organizer.name}} has put tickets aside for you. Pay with the button below to make them yours, and you'll get them in your inbox right away.</p>",
          fr: "<p>Bonjour {{buyer.first_name}},</p><p>{{organizer.name}} a mis des billets de côté pour vous. Payez avec le bouton ci-dessous pour les confirmer, vous les recevrez aussitôt par e-mail.</p>",
          de: "<p>Hallo {{buyer.first_name}},</p><p>{{organizer.name}} hat Tickets für dich zurückgelegt. Bezahle über den Button unten, dann gehören sie dir und du bekommst sie direkt per E-Mail.</p>",
        },
      },
      { id: id("rp"), type: "event" },
      { id: id("rp"), type: "payment_link" },
      {
        id: id("rp"), type: "text", html: {
          nl: "<p>Wil je toch niet? Dan hoef je niks te doen: de reservering vervalt vanzelf. Vragen kun je stellen door op deze mail te antwoorden.</p>",
          en: "<p>Changed your mind? Then you don't need to do anything: the reservation expires by itself. Questions? Just reply to this e-mail.</p>",
          fr: "<p>Vous avez changé d'avis ? Vous n'avez rien à faire : la réservation expire d'elle-même. Des questions ? Répondez simplement à cet e-mail.</p>",
          de: "<p>Doch keine Lust? Dann musst du nichts tun: Die Reservierung verfällt von selbst. Fragen? Antworte einfach auf diese E-Mail.</p>",
        },
      },
    ],
  },
  // After the organizer cancelled tickets (with or without a refund) or refunded money.
  // payload: { tickets?: number, refund?: cents }; see change.* in deliver.ts.
  order_cancelled: {
    version: 1,
    subject: {
      nl: "Je bestelling voor {{event.title}} is aangepast",
      en: "Your order for {{event.title}} has changed",
      fr: "Votre commande pour {{event.title}} a été modifiée",
      de: "Deine Bestellung für {{event.title}} wurde geändert",
    },
    blocks: [
      {
        id: id("ca"), type: "text", html: {
          nl: "<p>Hoi {{buyer.first_name}},</p><p>We hebben {{change.tickets}} voor {{event.title}} geannuleerd. {{change.refund_note}}</p><p>Op je bestelpagina zie je welke tickets nog geldig zijn.</p>",
          en: "<p>Hi {{buyer.first_name}},</p><p>We cancelled {{change.tickets}} for {{event.title}}. {{change.refund_note}}</p><p>Your order page shows which tickets are still valid.</p>",
          fr: "<p>Bonjour {{buyer.first_name}},</p><p>Nous avons annulé {{change.tickets}} pour {{event.title}}. {{change.refund_note}}</p><p>Votre page de commande indique les billets encore valables.</p>",
          de: "<p>Hallo {{buyer.first_name}},</p><p>Wir haben {{change.tickets}} für {{event.title}} storniert. {{change.refund_note}}</p><p>Auf deiner Bestellseite siehst du, welche Tickets noch gültig sind.</p>",
        },
      },
      { id: id("ca"), type: "order_link" },
      replyLine("ca"),
    ],
  },
  // The activity or timeslot does not go ahead and the buyer may choose (event_cancellation_start,
  // and once more 3 days before the deadline). A button per option; render.ts drops the ones whose
  // link is empty (no money to give back, no other timeslot, no donating).
  cancellation_choice: {
    version: 1,
    subject: {
      nl: "{{event.title}} gaat niet door: kies vóór {{choice.deadline}}",
      en: "{{event.title}} is cancelled: choose before {{choice.deadline}}",
      fr: "{{event.title}} est annulé : choisissez avant le {{choice.deadline}}",
      de: "{{event.title}} fällt aus: wähle bis {{choice.deadline}}",
    },
    blocks: [
      {
        id: id("cc"), type: "text", html: {
          nl: "<p>Hoi {{buyer.first_name}},</p><p>{{event.title}} op {{visit.date}} gaat helaas niet door. Je mag kiezen wat je wilt. {{choice.default}}</p>",
          en: "<p>Hi {{buyer.first_name}},</p><p>Unfortunately {{event.title}} on {{visit.date}} is cancelled. You can choose what you'd like. {{choice.default}}</p>",
          fr: "<p>Bonjour {{buyer.first_name}},</p><p>Malheureusement, {{event.title}} du {{visit.date}} est annulé. Vous pouvez choisir ce que vous préférez. {{choice.default}}</p>",
          de: "<p>Hallo {{buyer.first_name}},</p><p>{{event.title}} am {{visit.date}} fällt leider aus. Du kannst wählen, was du möchtest. {{choice.default}}</p>",
        },
      },
      { id: id("cc"), type: "button", label: { nl: "Geld terug", en: "Get my money back", fr: "Être remboursé(e)", de: "Geld zurück" }, href: "{{choice.refund_url}}" },
      { id: id("cc"), type: "button", label: { nl: "Kies een ander moment", en: "Pick another time", fr: "Choisir une autre date", de: "Anderen Termin wählen" }, href: "{{choice.move_url}}" },
      { id: id("cc"), type: "button", label: { nl: "Doneer het bedrag", en: "Donate the amount", fr: "Faire don du montant", de: "Betrag spenden" }, href: "{{choice.donate_url}}" },
      { id: id("cc"), type: "button", label: { nl: "Kies een tegoedbon", en: "Take a voucher", fr: "Choisir un bon", de: "Gutschein wählen" }, href: "{{choice.voucher_url}}" },
      replyLine("cc"),
    ],
  },
  // Extras bought later (add_order_extras), sent by mail_on_order_paid for the follow-up order;
  // order_id is the original order, payload.extras what was added.
  extras_bought: {
    version: 1,
    subject: {
      nl: "Toegevoegd aan je bestelling voor {{event.title}}",
      en: "Added to your order for {{event.title}}",
      fr: "Ajouté à votre commande pour {{event.title}}",
      de: "Zu deiner Bestellung für {{event.title}} hinzugefügt",
    },
    blocks: [
      {
        id: id("eb"), type: "text", html: {
          nl: "<p>Hoi {{buyer.first_name}},</p><p>Gelukt: {{change.extras}} staat bij je bestelling voor {{event.title}}. Je vindt het terug op je bestelpagina.</p>",
          en: "<p>Hi {{buyer.first_name}},</p><p>Done: {{change.extras}} is now part of your order for {{event.title}}. You'll find it on your order page.</p>",
          fr: "<p>Bonjour {{buyer.first_name}},</p><p>C'est fait : {{change.extras}} fait partie de votre commande pour {{event.title}}. Vous le retrouverez sur votre page de commande.</p>",
          de: "<p>Hallo {{buyer.first_name}},</p><p>Erledigt: {{change.extras}} gehört jetzt zu deiner Bestellung für {{event.title}}. Du findest es auf deiner Bestellseite.</p>",
        },
      },
      { id: id("eb"), type: "order_link" },
      replyLine("eb"),
    ],
  },
  // A voucher per cancelled ticket instead of money back (a cancellation's choice, or the organizer
  // cancelling one order): payload.codes, each one free ticket, once.
  order_voucher: {
    version: 1,
    subject: {
      nl: "Je tegoedbon voor {{organizer.name}}",
      en: "Your voucher for {{organizer.name}}",
      fr: "Votre bon pour {{organizer.name}}",
      de: "Dein Gutschein für {{organizer.name}}",
    },
    blocks: [
      {
        id: id("vc"), type: "text", html: {
          nl: "<p>Hoi {{buyer.first_name}},</p><p>Je tickets voor {{event.title}} zijn omgezet in {{voucher.count}}. Vul een code in bij het bestellen, dan is één ticket gratis. Elke code werkt één keer.</p><h2>{{voucher.codes}}</h2>",
          en: "<p>Hi {{buyer.first_name}},</p><p>Your tickets for {{event.title}} have been turned into {{voucher.count}}. Enter a code when you order and one ticket is free. Each code works once.</p><h2>{{voucher.codes}}</h2>",
          fr: "<p>Bonjour {{buyer.first_name}},</p><p>Vos billets pour {{event.title}} ont été convertis en {{voucher.count}}. Saisissez un code lors de votre commande et un billet est gratuit. Chaque code fonctionne une fois.</p><h2>{{voucher.codes}}</h2>",
          de: "<p>Hallo {{buyer.first_name}},</p><p>Deine Tickets für {{event.title}} wurden in {{voucher.count}} umgewandelt. Gib beim Bestellen einen Code ein, dann ist ein Ticket gratis. Jeder Code gilt einmal.</p><h2>{{voucher.codes}}</h2>",
        },
      },
      { id: id("vc"), type: "shop_button" },
      replyLine("vc"),
    ],
  },
  order_refunded: {
    version: 1,
    subject: {
      nl: "Je krijgt geld terug van {{organizer.name}}",
      en: "You're getting money back from {{organizer.name}}",
      fr: "{{organizer.name}} vous rembourse",
      de: "Du bekommst Geld zurück von {{organizer.name}}",
    },
    blocks: [
      {
        id: id("re"), type: "text", html: {
          nl: "<p>Hoi {{buyer.first_name}},</p><p>Je krijgt {{change.refund}} terug voor je bestelling voor {{event.title}}. Het geld gaat terug naar de rekening of kaart waarmee je betaalde, en staat er meestal binnen een paar werkdagen op.</p>",
          en: "<p>Hi {{buyer.first_name}},</p><p>You're getting {{change.refund}} back for your order for {{event.title}}. It goes back to the account or card you paid with, usually within a few working days.</p>",
          fr: "<p>Bonjour {{buyer.first_name}},</p><p>Vous recevez {{change.refund}} en retour pour votre commande pour {{event.title}}. Le montant revient sur le compte ou la carte utilisés pour payer, généralement en quelques jours ouvrés.</p>",
          de: "<p>Hallo {{buyer.first_name}},</p><p>Du bekommst {{change.refund}} für deine Bestellung für {{event.title}} zurück. Das Geld geht auf das Konto oder die Karte, mit der du bezahlt hast, meist innerhalb weniger Werktage.</p>",
        },
      },
      replyLine("re"),
    ],
  },
  // The waitlist (CLAUDE.md, "Waitlist"), also for who signed up before the sale (the open mail
  // then says the sale has started). payload.token is the entry's; a block that links to
  // an empty {{..._url}} is left out (render.ts), so the consent button only shows with a
  // signup token and the decline link only when the entry has other timeslots.
  waitlist_joined: {
    version: 1,
    subject: {
      nl: "Je staat op de wachtlijst voor {{event.title}}",
      en: "You're on the waitlist for {{event.title}}",
      fr: "Vous êtes sur la liste d'attente pour {{event.title}}",
      de: "Du stehst auf der Warteliste für {{event.title}}",
    },
    preheader: {
      nl: "Zijn er tickets voor je, dan hoor je het van ons.",
      en: "If there are tickets for you, we'll let you know.",
      fr: "S'il y a des billets pour vous, on vous prévient.",
      de: "Gibt es Tickets für dich, melden wir uns.",
    },
    blocks: [
      {
        id: id("wj"), type: "text", html: {
          nl: "<p>Hoi {{buyer.first_name}},</p><p>Je staat op de wachtlijst. Zijn er tickets voor je, dan krijg je een mail om te bestellen.</p>",
          en: "<p>Hi {{buyer.first_name}},</p><p>You're on the waitlist. If there are tickets for you, you'll get an e-mail to order.</p>",
          fr: "<p>Bonjour {{buyer.first_name}},</p><p>Vous êtes sur la liste d'attente. S'il y a des billets pour vous, vous recevrez un e-mail pour commander.</p>",
          de: "<p>Hallo {{buyer.first_name}},</p><p>Du stehst auf der Warteliste. Gibt es Tickets für dich, bekommst du eine E-Mail zum Bestellen.</p>",
        },
      },
      { id: id("wj"), type: "event" },
      {
        id: id("wj"), type: "text", html: {
          nl: "<p>Je wilt ook nieuws van {{organizer.name}} ontvangen. Bevestig dat even, dan weten we zeker dat het jouw adres is.</p><p><a href=\"{{signup.confirm_url}}\">Ja, houd me op de hoogte</a></p>",
          en: "<p>You'd also like news from {{organizer.name}}. Please confirm, so we know it's your address.</p><p><a href=\"{{signup.confirm_url}}\">Yes, keep me posted</a></p>",
          fr: "<p>Vous souhaitez aussi recevoir les nouvelles de {{organizer.name}}. Confirmez-le, pour que nous sachions que c'est bien votre adresse.</p><p><a href=\"{{signup.confirm_url}}\">Oui, tenez-moi informé(e)</a></p>",
          de: "<p>Du möchtest auch Neuigkeiten von {{organizer.name}} bekommen. Bestätige das kurz, damit wir wissen, dass es deine Adresse ist.</p><p><a href=\"{{signup.confirm_url}}\">Ja, halte mich auf dem Laufenden</a></p>",
        },
      },
      { id: id("wj"), type: "button", label: { nl: "Haal me van de lijst", en: "Take me off the list", fr: "Me retirer de la liste", de: "Nimm mich von der Liste" }, href: "{{waitlist.leave_url}}" },
    ],
  },
  waitlist_offer: {
    version: 1,
    subject: {
      nl: "Er is plek voor je: {{event.title}}",
      en: "There's a place for you: {{event.title}}",
      fr: "Il y a une place pour vous : {{event.title}}",
      de: "Es gibt einen Platz für dich: {{event.title}}",
    },
    preheader: {
      nl: "We houden je tickets even vast. Betaal op tijd, dan zijn ze van jou.",
      en: "We're holding your tickets for a while. Pay in time and they're yours.",
      fr: "Nous gardons vos billets un moment. Payez à temps et ils sont à vous.",
      de: "Wir halten deine Tickets kurz für dich fest. Bezahle rechtzeitig, dann gehören sie dir.",
    },
    blocks: [
      {
        id: id("wo"), type: "text", html: {
          nl: "<p>Hoi {{buyer.first_name}},</p><p>Goed nieuws: er is plek. We houden {{order.tickets}} voor je vast tot {{order.pay_before}}. Betaal je niet op tijd, dan gaat je plek naar de volgende op de lijst.</p>",
          en: "<p>Hi {{buyer.first_name}},</p><p>Good news: there's a place. We're holding {{order.tickets}} for you until {{order.pay_before}}. If you don't pay in time, your place goes to the next person on the list.</p>",
          fr: "<p>Bonjour {{buyer.first_name}},</p><p>Bonne nouvelle : il y a de la place. Nous gardons {{order.tickets}} pour vous jusqu'au {{order.pay_before}}. Si vous ne payez pas à temps, votre place passe à la personne suivante sur la liste.</p>",
          de: "<p>Hallo {{buyer.first_name}},</p><p>Gute Nachricht: Es gibt einen Platz. Wir halten {{order.tickets}} bis {{order.pay_before}} für dich fest. Bezahlst du nicht rechtzeitig, geht dein Platz an die nächste Person auf der Liste.</p>",
        },
      },
      { id: id("wo"), type: "event" },
      { id: id("wo"), type: "payment_link" },
      {
        id: id("wo"), type: "text", html: {
          nl: "<p>Past dit tijdslot toch niet? <a href=\"{{waitlist.decline_url}}\">Geef je plek door</a>, dan blijf je op de lijst voor je andere tijden.</p>",
          en: "<p>This timeslot doesn't work for you after all? <a href=\"{{waitlist.decline_url}}\">Pass your place on</a> and you'll stay on the list for your other times.</p>",
          fr: "<p>Ce créneau ne vous convient pas ? <a href=\"{{waitlist.decline_url}}\">Cédez votre place</a> et vous restez sur la liste pour vos autres horaires.</p>",
          de: "<p>Passt dieser Zeitslot doch nicht? <a href=\"{{waitlist.decline_url}}\">Gib deinen Platz weiter</a>, dann bleibst du für deine anderen Zeiten auf der Liste.</p>",
        },
      },
    ],
  },
  waitlist_open: {
    version: 1,
    subject: {
      nl: "Er zijn tickets voor {{event.title}}",
      en: "Tickets are available for {{event.title}}",
      fr: "Des billets sont disponibles pour {{event.title}}",
      de: "Es gibt Tickets für {{event.title}}",
    },
    preheader: {
      nl: "Wie het eerst bestelt, heeft ze.",
      en: "First come, first served.",
      fr: "Premier arrivé, premier servi.",
      de: "Wer zuerst bestellt, bekommt sie.",
    },
    blocks: [
      {
        id: id("wn"), type: "text", html: {
          nl: "<p>Hoi {{buyer.first_name}},</p><p>Er zijn tickets voor {{event.title}}. Wie het eerst bestelt, heeft ze.</p>",
          en: "<p>Hi {{buyer.first_name}},</p><p>Tickets are available for {{event.title}}. First come, first served.</p>",
          fr: "<p>Bonjour {{buyer.first_name}},</p><p>Des billets sont disponibles pour {{event.title}}. Premier arrivé, premier servi.</p>",
          de: "<p>Hallo {{buyer.first_name}},</p><p>Es gibt Tickets für {{event.title}}. Wer zuerst bestellt, bekommt sie.</p>",
        },
      },
      { id: id("wn"), type: "shop_button" },
      {
        id: id("wn"), type: "text", html: {
          nl: "<p>Hoef je niet meer? <a href=\"{{waitlist.leave_url}}\">Haal me van de lijst</a></p>",
          en: "<p>No longer needed? <a href=\"{{waitlist.leave_url}}\">Take me off the list</a></p>",
          fr: "<p>Plus besoin ? <a href=\"{{waitlist.leave_url}}\">Me retirer de la liste</a></p>",
          de: "<p>Brauchst du es nicht mehr? <a href=\"{{waitlist.leave_url}}\">Nimm mich von der Liste</a></p>",
        },
      },
    ],
  },
  signup_confirm: {
    version: 1,
    subject: {
      nl: "Bevestig je aanmelding bij {{organizer.name}}",
      en: "Confirm your signup with {{organizer.name}}",
      fr: "Confirmez votre inscription chez {{organizer.name}}",
      de: "Bestätige deine Anmeldung bei {{organizer.name}}",
    },
    blocks: [
      {
        id: id("sc"), type: "text", html: {
          nl: "<p>Hoi,</p><p>Klik op de knop om te bevestigen dat je nieuws van {{organizer.name}} wilt ontvangen. Afmelden kan altijd weer, onderaan elke mail. Heb je je niet aangemeld? Dan kun je deze mail negeren.</p>",
          en: "<p>Hi,</p><p>Click the button to confirm you'd like news from {{organizer.name}}. You can unsubscribe at any time, at the bottom of every e-mail. Didn't sign up? Just ignore this e-mail.</p>",
          fr: "<p>Bonjour,</p><p>Cliquez sur le bouton pour confirmer que vous souhaitez recevoir les nouvelles de {{organizer.name}}. Vous pouvez vous désinscrire à tout moment, en bas de chaque e-mail. Vous ne vous êtes pas inscrit(e) ? Ignorez simplement cet e-mail.</p>",
          de: "<p>Hallo,</p><p>Klicke auf den Button, um zu bestätigen, dass du Neuigkeiten von {{organizer.name}} bekommen möchtest. Abmelden kannst du dich jederzeit unten in jeder E-Mail. Nicht angemeldet? Dann ignoriere diese E-Mail einfach.</p>",
        },
      },
      { id: id("sc"), type: "button", label: { nl: "Ja, houd me op de hoogte", en: "Yes, keep me posted", fr: "Oui, tenez-moi informé(e)", de: "Ja, halte mich auf dem Laufenden" }, href: "{{signup.confirm_url}}" },
    ],
  },
  // Self-service (order-self-service): the code that unlocks changing an order. payload.code and
  // payload.path (the page that asked); the button opens that page with the code filled in.
  manage_code: {
    version: 1,
    subject: {
      nl: "Je code is {{manage.code}}",
      en: "Your code is {{manage.code}}",
      fr: "Votre code est {{manage.code}}",
      de: "Dein Code ist {{manage.code}}",
    },
    preheader: {
      nl: "Hiermee pas je je bestelling voor {{event.title}} aan.",
      en: "Use it to change your order for {{event.title}}.",
      fr: "Il vous permet de modifier votre commande pour {{event.title}}.",
      de: "Damit änderst du deine Bestellung für {{event.title}}.",
    },
    blocks: [
      {
        id: id("mc"), type: "text", html: {
          nl: "<p>Hoi {{buyer.first_name}},</p><p>Je wilt iets aanpassen aan je bestelling voor {{event.title}}. Vul deze code in:</p><h1>{{manage.code}}</h1><p>Hij werkt 30 minuten. Heb je niks aangevraagd? Dan kun je deze mail negeren, er verandert niks.</p>",
          en: "<p>Hi {{buyer.first_name}},</p><p>You'd like to change your order for {{event.title}}. Enter this code:</p><h1>{{manage.code}}</h1><p>It works for 30 minutes. Didn't ask for it? Just ignore this e-mail, nothing changes.</p>",
          fr: "<p>Bonjour {{buyer.first_name}},</p><p>Vous souhaitez modifier votre commande pour {{event.title}}. Saisissez ce code :</p><h1>{{manage.code}}</h1><p>Il est valable 30 minutes. Vous n'avez rien demandé ? Ignorez cet e-mail, rien ne change.</p>",
          de: "<p>Hallo {{buyer.first_name}},</p><p>Du möchtest deine Bestellung für {{event.title}} ändern. Gib diesen Code ein:</p><h1>{{manage.code}}</h1><p>Er gilt 30 Minuten. Nichts angefordert? Dann ignoriere diese E-Mail einfach, es ändert sich nichts.</p>",
        },
      },
      { id: id("mc"), type: "button", label: { nl: "Vul de code in", en: "Enter the code", fr: "Saisir le code", de: "Code eingeben" }, href: "{{manage.url}}" },
    ],
  },
  // A ticket on someone's name and address (the buyer typed it): only their ticket, attached, and no
  // order block, since the order and its link belong to the buyer. payload.name is the holder's name.
  ticket_holder: {
    version: 1,
    subject: {
      nl: "Je ticket voor {{event.title}}",
      en: "Your ticket for {{event.title}}",
      fr: "Votre billet pour {{event.title}}",
      de: "Dein Ticket für {{event.title}}",
    },
    preheader: {
      nl: "Er staat een ticket op jouw naam.",
      en: "There's a ticket in your name.",
      fr: "Un billet est à votre nom.",
      de: "Ein Ticket ist auf deinen Namen ausgestellt.",
    },
    blocks: [
      {
        id: id("th"), type: "text", html: {
          nl: "<p>Hoi {{buyer.first_name}},</p><p>Er staat een ticket op jouw naam voor {{event.title}}. Je vindt het in de bijlage, of download het hieronder.</p>",
          en: "<p>Hi {{buyer.first_name}},</p><p>There's a ticket in your name for {{event.title}}. You'll find it attached, or download it below.</p>",
          fr: "<p>Bonjour {{buyer.first_name}},</p><p>Un billet pour {{event.title}} est à votre nom. Vous le trouverez en pièce jointe, ou téléchargez-le ci-dessous.</p>",
          de: "<p>Hallo {{buyer.first_name}},</p><p>Für {{event.title}} ist ein Ticket auf deinen Namen ausgestellt. Du findest es im Anhang oder lädst es hier herunter.</p>",
        },
      },
      { id: id("th"), type: "button", label: { nl: "Download je ticket", en: "Download your ticket", fr: "Télécharger votre billet", de: "Ticket herunterladen" }, href: "{{ticket.pdf_url}}" },
      { id: id("th"), type: "event" },
      {
        id: id("th"), type: "text", html: {
          nl: "<p><strong>Bij de ingang</strong> laat je je ticket zien op je telefoon, of je neemt het geprint mee. De code wordt één keer gescand, dus deel hem niet online.</p>",
          en: "<p><strong>At the entrance</strong> show your ticket on your phone, or bring it printed. Its code is scanned once, so don't share it online.</p>",
          fr: "<p><strong>À l'entrée</strong>, montrez votre billet sur votre téléphone ou apportez-le imprimé. Son code est scanné une seule fois : ne le partagez pas en ligne.</p>",
          de: "<p><strong>Am Eingang</strong> zeigst du dein Ticket auf dem Handy oder bringst es ausgedruckt mit. Der Code wird einmal gescannt, also teile ihn nicht online.</p>",
        },
      },
    ],
  },
};

export interface Starter {
  key: string;
  name: { nl: string; en: string };
  kind: "transactional" | "marketing";
  document: MailDocument;
}

export const STARTER_TEMPLATES: Starter[] = [
  {
    key: "reminder",
    name: { nl: "Herinnering", en: "Reminder" },
    kind: "transactional",
    document: {
      version: 1,
      subject: { nl: "Bijna zover: {{event.title}}", en: "Almost time: {{event.title}}" },
      preheader: { nl: "Wanneer, waar en je tickets op een rij.", en: "When, where and your tickets in one place." },
      blocks: [
        { id: id("st"), type: "text", html: { nl: "<p>Hoi {{buyer.first_name}},</p><p>Nog even en dan is het zover! Hieronder staat alles op een rij: wanneer, waar en je tickets.</p>", en: "<p>Hi {{buyer.first_name}},</p><p>It's almost time! Below you'll find it all in one place: when, where and your tickets.</p>" } },
        { id: id("st"), type: "event" },
        { id: id("st"), type: "order_link" },
        { id: id("st"), type: "extras" },
        { id: id("st"), type: "text", html: { nl: "<p><strong>Tip:</strong> open je tickets alvast voordat je vertrekt. Dan heb je ze ook zonder bereik bij de hand en sta je zo binnen.</p><p>Tot snel!</p>", en: "<p><strong>Tip:</strong> open your tickets before you head out. Then you have them even without signal, and you're in in no time.</p><p>See you soon!</p>" } },
      ],
    },
  },
  // Transactional mail goes to everyone with a ticket, consent or not: no promotion in it (an
  // event_promo block only renders in marketing mail, see render.ts).
  {
    key: "thanks",
    name: { nl: "Bedankt voor je bezoek", en: "Thanks for coming" },
    kind: "transactional",
    document: {
      version: 1,
      subject: { nl: "Bedankt voor je komst!", en: "Thanks for coming!" },
      preheader: { nl: "Wat vond je ervan?", en: "What did you think?" },
      blocks: [
        { id: id("st"), type: "text", html: { nl: "<p>Hoi {{buyer.first_name}},</p><p>Wat leuk dat je erbij was bij {{event.title}}. We hopen dat je genoten hebt!</p><p>Wat vond je ervan? Antwoord gerust op deze mail, {{organizer.name}} leest alles.</p>", en: "<p>Hi {{buyer.first_name}},</p><p>So great that you came to {{event.title}}. We hope you enjoyed it!</p><p>What did you think? Feel free to reply to this e-mail, {{organizer.name}} reads every message.</p>" } },
      ],
    },
  },
  {
    key: "payment_reminder",
    name: { nl: "Betaalherinnering reservering", en: "Reservation payment reminder" },
    kind: "transactional",
    document: {
      version: 1,
      subject: { nl: "Je reservering verloopt bijna", en: "Your reservation expires soon" },
      preheader: { nl: "Nog even betalen, dan zijn je tickets van jou.", en: "Pay now and the tickets are yours." },
      blocks: [
        { id: id("st"), type: "text", html: { nl: "<p>Hoi {{buyer.first_name}},</p><p>Je reservering voor {{event.title}} is nog niet betaald. Rond hem af via de knop, anders vervalt hij.</p>", en: "<p>Hi {{buyer.first_name}},</p><p>Your reservation for {{event.title}} hasn't been paid yet. Complete it with the button, or it will expire.</p>" } },
        { id: id("st"), type: "payment_link" },
        { id: id("st"), type: "text", html: { nl: "<p>Wil je toch niet? Dan hoef je niks te doen: de reservering vervalt vanzelf.</p>", en: "<p>Changed your mind? Then you don't need to do anything: the reservation expires by itself.</p>" } },
      ],
    },
  },
  // For a service message (mail_campaigns.kind = 'service'): something changes about the visit.
  {
    key: "change",
    name: { nl: "Wijziging", en: "Change" },
    kind: "transactional",
    document: {
      version: 1,
      subject: { nl: "Let op: wijziging voor {{event.title}}", en: "Please note: a change for {{event.title}}" },
      blocks: [
        { id: id("st"), type: "text", html: { nl: "<p>Hoi {{buyer.first_name}},</p><p>Er verandert iets aan {{event.title}} op {{visit.date}}.</p>", en: "<p>Hi {{buyer.first_name}},</p><p>Something is changing about {{event.title}} on {{visit.date}}.</p>" } },
        { id: id("st"), type: "text", html: { nl: "<p><strong>Wat verandert:</strong> </p>", en: "<p><strong>What changes:</strong> </p>" } },
        { id: id("st"), type: "text", html: { nl: "<p><strong>Wat blijft hetzelfde:</strong> </p>", en: "<p><strong>What stays the same:</strong> </p>" } },
        { id: id("st"), type: "text", html: { nl: "<p><strong>Wat moet je doen:</strong> </p>", en: "<p><strong>What you need to do:</strong> </p>" } },
        { id: id("st"), type: "event" },
        { id: id("st"), type: "order_link" },
      ],
    },
  },
  {
    key: "welcome",
    name: { nl: "Welkom na aanmelden", en: "Welcome after signup" },
    kind: "marketing",
    document: {
      version: 1,
      subject: { nl: "Welkom bij {{organizer.name}}!", en: "Welcome to {{organizer.name}}!" },
      blocks: [
        { id: id("st"), type: "text", html: { nl: "<p>Hoi {{buyer.first_name}},</p><p>Leuk dat je erbij bent! Je hoort als eerste wanneer er iets nieuws aankomt. Dit staat er nu al op de planning:</p>", en: "<p>Hi {{buyer.first_name}},</p><p>Great to have you! You'll be the first to hear when something new is coming. This is already on the calendar:</p>" } },
        { id: id("st"), type: "event_promo", mode: "upcoming", max: 3 },
      ],
    },
  },
  {
    key: "new_event",
    name: { nl: "Nieuwe activiteit", en: "New event" },
    kind: "marketing",
    document: {
      version: 1,
      subject: { nl: "Nieuw: {{event.title}}", en: "New: {{event.title}}" },
      blocks: [
        { id: id("st"), type: "text", html: { nl: "<p>Hoi {{buyer.first_name}},</p><p>We hebben iets nieuws voor je, en jij hoort het als een van de eersten. De kaartverkoop is net begonnen!</p>", en: "<p>Hi {{buyer.first_name}},</p><p>We've got something new, and you're among the first to hear. Tickets just went on sale!</p>" } },
        { id: id("st"), type: "event" },
        { id: id("st"), type: "shop_button" },
      ],
    },
  },
  // For an invitation (mail_campaigns.kind = 'invitation', "Codes mailen"): every recipient gets a
  // personal code that works once. discount_id may stay empty: the campaign's discount is used then.
  {
    key: "invitation",
    name: { nl: "Uitnodiging", en: "Invitation" },
    kind: "marketing",
    document: {
      version: 1,
      subject: { nl: "Je bent uitgenodigd door {{organizer.name}}", en: "You're invited by {{organizer.name}}" },
      preheader: { nl: "Met een persoonlijke code, alleen voor jou.", en: "With a personal code, just for you." },
      blocks: [
        { id: id("st"), type: "text", html: { nl: "<p>Hoi {{buyer.first_name}},</p><p>We willen je graag uitnodigen. Met de code hieronder regel je je tickets in een paar klikken.</p>", en: "<p>Hi {{buyer.first_name}},</p><p>We'd love to invite you. With the code below you'll have your tickets in a few clicks.</p>" } },
        { id: id("st"), type: "discount_code", discount_id: "" },
        { id: id("st"), type: "text", html: { nl: "<p>Tot snel!</p>", en: "<p>See you soon!</p>" } },
      ],
    },
  },
  {
    key: "newsletter",
    name: { nl: "Nieuwsbrief", en: "Newsletter" },
    kind: "marketing",
    document: {
      version: 1,
      subject: { nl: "Nieuws van {{organizer.name}}", en: "News from {{organizer.name}}" },
      blocks: [
        { id: id("st"), type: "heading", level: 1, text: { nl: "Wat staat er op de planning?", en: "What's coming up?" } },
        { id: id("st"), type: "text", html: { nl: "<p>Hoi {{buyer.first_name}},</p><p>Schrijf hier je verhaal.</p>", en: "<p>Hi {{buyer.first_name}},</p><p>Write your story here.</p>" } },
        { id: id("st"), type: "event_promo", mode: "upcoming", max: 3 },
      ],
    },
  },
];
